import { useEffect, useState } from "react";
import { Event } from "../../data/models/bracket_api/Event";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import LinearProgress from '@mui/material/LinearProgress';
import { Alert, Divider, Snackbar, Theme, useMediaQuery, useTheme } from "@mui/material";
import BracketIcon from "../../assets/images/bracket.svg";
import BracketIconDark from "../../assets/images/bracket_black.svg"
import Button from "@mui/material/Button";
import Tooltip from '@mui/material/Tooltip';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import { manage_subscribe } from "../../apis/Mailchimp";

type TUseTimer = {
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
};

const formatNumber = (num: number) => {
    return num < 10 ? `0${num}` : `${num}`;
};

const DAYS_IN_MS = 1000 * 60 * 60 * 24;
const HOURS_IN_MS = 1000 * 60 * 60;
const MIN_IN_MS = 1000 * 60;
const SEC_IN_MS = 1000;

const getTimeDiff = (diffInMSec: number): TUseTimer => {
    let diff = diffInMSec;
    const days = Math.floor(diff / DAYS_IN_MS);
    diff -= days * DAYS_IN_MS;
    const hours = Math.floor(diff / HOURS_IN_MS);
    diff -= hours * HOURS_IN_MS;
    const minutes = Math.floor(diff / MIN_IN_MS);
    diff -= minutes * MIN_IN_MS;
    const seconds = Math.floor(diff / SEC_IN_MS);
    return {
        days: days as unknown as string,
        hours: formatNumber(hours),
        minutes: formatNumber(minutes),
        seconds: formatNumber(seconds),
    };
};

const useTimer = (targetTime: number): TUseTimer => {
    const [timeLeft, setTimeLeft] = useState(targetTime - Date.now());

    useEffect(() => {
        const id = setTimeout(() => {
            // We can set conditions here like timeLeft > 0
            setTimeLeft((prev) => prev - 1000);
        }, 1000);

        return () => {
            clearTimeout(id);
        };
    });

    return getTimeDiff(timeLeft);
};

type BracketsOpenContainerProps = {
    event: Event | undefined
}

export default function BracketsOpenContainer(props: Readonly<BracketsOpenContainerProps>) {
    const theme = useTheme();
    const darkMode = theme.palette.mode === 'dark';
    const [isMdOrUp, setIsMdOrUp] = useState(useMediaQuery((theme: Theme) => theme.breakpoints.up('md')));

    // Standard Bracket
    const [stdDeadline] = useState<Date>(new Date(props.event?.open_date || new Date()));
    const stdTime = useTimer(stdDeadline.getTime());
    const [stdBracketsOpen] = useState(!props.event?.picks_locked || false);
    const [stdBracketsOpenState, setStdBracketsOpenState] = useState<"countdown" | "late" | "open" | "closed">("countdown");

    // Pro Mod Bracket
    const [proModDeadline] = useState<Date>(new Date(props.event?.pro_mod_open_date || new Date()));
    const proModTime = useTimer(proModDeadline.getTime());
    const [proModBracketsOpen] = useState(!props.event?.pro_mod_picks_locked || false);
    const [proModBracketsOpenState, setProModBracketsOpenState] = useState<"countdown" | "late" | "open" | "closed">("countdown");

    const [open, setOpen] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");
    const [severity, setSeverity] = useState<"success" | "info" | "warning" | "error" | undefined>("error");

    useEffect(() => {
        // Standard Bracket State
        if (stdBracketsOpen) {
            setStdBracketsOpenState("open")
        }
        else if (!stdBracketsOpen && props.event?.close_date && new Date() < new Date(props.event?.close_date) && new Date() > new Date(props.event?.open_date)) {
            setStdBracketsOpenState("late")
        }
        else if (!stdBracketsOpen && props.event?.close_date && new Date() > new Date(props.event?.close_date)) {
            setStdBracketsOpenState("closed")
        }
        else {
            setStdBracketsOpenState("countdown")
        }
        // Pro Mod Bracket State
        if (proModBracketsOpen) {
            setProModBracketsOpenState("open")
        }
        else if (!proModBracketsOpen && props.event?.pro_mod_close_date && new Date() < new Date(props.event?.pro_mod_close_date) && props.event?.pro_mod_open_date && new Date() > new Date(props.event?.pro_mod_open_date)) {
            setProModBracketsOpenState("late")
        }
        else if (!proModBracketsOpen && props.event?.pro_mod_close_date && new Date() > new Date(props.event?.pro_mod_close_date)) {
            setProModBracketsOpenState("closed")
        }
        else {
            setProModBracketsOpenState("countdown")
        }
        const handleResize = () => {
            setIsMdOrUp(window.innerWidth >= 960);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [stdBracketsOpen, proModBracketsOpen, props.event]);

    const handleEmailReminder = (bracketType: string) => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const user_id = user.id;
        const email_subscribe = true;
        const sms_subscribe = false;
        if (user_id) {
            const user_id_num = parseInt(user_id, 10);
            manage_subscribe(user_id_num, email_subscribe, sms_subscribe).then((res) => {
                if (res.succeeded) {
                    setMessage("Email reminder signup successful");
                    setOpen(true);
                    setSeverity("success");
                }
                else {
                    setMessage("Email reminder signup failed");
                    setOpen(true);
                    setSeverity("error");
                }
            });
        } else {
            window.location.href = "/signin";
        }
    };
    const handleTextReminder = (bracketType: string) => {
        window.open('https://mailchi.mp/2a9d16e6744b/sms-signup', '_blank');
    };

    const getBracketButtonText = (type: 'standard' | 'pro_mod', isTopMoments?: boolean) => {
        if (type === 'standard') {
            if (stdBracketsOpenState === 'countdown') {
                const day = new Date(stdDeadline).toLocaleDateString(undefined, { weekday: 'long' });
                const time = new Date(stdDeadline).toLocaleTimeString(undefined, { hour12: true, timeStyle: 'short' });
                return 'Opens at ' + day + ' ' + time;
            }
            else if (stdBracketsOpenState === 'late') {
                return 'Opens Soon';
            }
            else if (stdBracketsOpenState === 'open') {
                return isTopMoments ? 'Fill Out Top Moments Bracket' : 'Fill Out Standard Bracket';
            }
            else if (stdBracketsOpenState === 'closed') {
                return 'Bracket Closed';
            }
        }
        else if (type === 'pro_mod') {
            if (proModBracketsOpenState === 'countdown') {
                const day = new Date(proModDeadline).toLocaleDateString(undefined, { weekday: 'long' });
                const time = new Date(proModDeadline).toLocaleTimeString(undefined, { hour12: true, timeStyle: 'short' });
                return 'Opens at ' + day + ' ' + time;
            }
            else if (proModBracketsOpenState === 'late') {
                return 'Opens Soon';
            }
            else if (proModBracketsOpenState === 'open') {
                return 'Fill Out Pro Mod Bracket';
            }
            else if (proModBracketsOpenState === 'closed') {
                return 'Bracket Closed';
            }
        }
    }

    // Helper to render a single bracket card
    const renderBracketCard = (type: 'standard' | 'pro_mod', darkMode: boolean, isMdOrUp: boolean) => {
        const isProMod = type === 'pro_mod';
        const isTopMoments = !isProMod && !!event?.top_moments_bracket;
        const time = isProMod ? proModTime : stdTime;
        const bracketsOpenState = isProMod ? proModBracketsOpenState : stdBracketsOpenState;
        const closeDate = isProMod ? event?.pro_mod_close_date : event?.close_date;
        const openDate = isProMod ? event?.pro_mod_open_date : event?.open_date;
        const name = isProMod ? 'PRO MOD BRACKET' : (isTopMoments ? 'TOP MOMENTS BRACKET' : 'STANDARD BRACKET');
        const buttonHref = isProMod
            ? `/bracket/pro-mod/?${encodeURIComponent("event_id=" + event?.id + "&league_id=1")}`
            : isTopMoments
                ? `/bracket/top-moments/?${encodeURIComponent("event_id=" + event?.id + "&league_id=1")}`
                : `/bracket/?${encodeURIComponent("event_id=" + event?.id + "&league_id=1")}`;
        const cardBg = isProMod ? (darkMode ? '#c40000' : '#e53935') : (darkMode ? '#486ce7' : '#5c7cfa');
        const cardText = '#fff';
        const buttonBg = !isProMod ? '#240180' : '#640000';
        const buttonBgSecondary = !isProMod ? '#242cc0' : '#890000';
        const buttonShadow = isProMod ? '0 4px 16px #a30000' : '0 4px 16px #2a3eb1';
        const buttonHover = isProMod ? '#a30000' : '#2a3eb1';
        return (
            <div
                style={{
                    background: cardBg,
                    borderRadius: 24,
                    padding: isMdOrUp ? 36 : 18,
                    minHeight: 340,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
                    marginBottom: !isMdOrUp && isProMod ? 0 : (!isMdOrUp ? 16 : 0),
                    width: '100%',
                }}
            >
                <Typography
                    variant={'h5'}
                    fontWeight="bold"
                    sx={{
                        color: cardText,
                        letterSpacing: isMdOrUp ? 1 : 0,
                        mb: 0.5,
                    }}
                >
                    {name}
                </Typography>
                <Typography
                    variant={isMdOrUp ? 'h6' : 'body1'}
                    sx={{ color: cardText, mb: 3, fontWeight: 400 }}
                >
                    {bracketsOpenState === 'countdown' && (
                        <>Opens In: <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{time.days}:{time.hours}:{time.minutes}:{time.seconds}</span></>
                    )}
                    {bracketsOpenState === 'late' && 'Opens Soon'}
                    {bracketsOpenState === 'open' && (
                        <>
                            {isTopMoments && 'Fill out Top Moments Bracket. '}
                            Closes At: <span style={{ fontWeight: 600 }}>{closeDate ? (() => {
                                const d = new Date(closeDate);
                                const day = d.toLocaleDateString(undefined, { weekday: 'long' });
                                const time = d.toLocaleTimeString(undefined, { hour12: true, timeStyle: 'short' });
                                return `${day} ${time}`;
                            })() : ''}</span>
                        </>
                    )}
                    {bracketsOpenState === 'closed' && 'Closed'}
                </Typography>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 18, alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        disabled={bracketsOpenState !== 'open'}
                        sx={{
                            background: buttonBg,
                            color: '#fff',
                            fontSize: isMdOrUp ? 20 : 16,
                            borderRadius: 12,
                            minWidth: 260,
                            minHeight: 48,
                            boxShadow: buttonShadow,
                            mb: 1,
                            ':hover': {
                                background: buttonHover,
                            },
                        }}
                        onClick={() => { if (event && bracketsOpenState === 'open') window.location.href = buttonHref; }}
                    >
                        {getBracketButtonText(type, isTopMoments)}
                    </Button>
                    <Button
                        variant="contained"
                        sx={{
                            background: buttonBgSecondary,
                            color: '#fff',
                            fontSize: isMdOrUp ? 20 : 16,
                            borderRadius: 12,
                            minWidth: 260,
                            minHeight: 48,
                            boxShadow: buttonShadow,
                            mb: 1,
                            ':hover': {
                                background: buttonHover,
                            },
                        }}
                        onClick={() => handleEmailReminder(type)}
                    >
                        Email Reminders
                    </Button>
                    <Button
                        variant="contained"
                        sx={{
                            background: buttonBgSecondary,
                            color: '#fff',
                            fontSize: isMdOrUp ? 20 : 16,
                            borderRadius: 12,
                            minWidth: 260,
                            minHeight: 48,
                            boxShadow: buttonShadow,
                            ':hover': {
                                background: buttonHover,
                            },
                        }}
                        onClick={() => handleTextReminder(type)}
                    >
                        Text Reminders
                    </Button>
                </div>
            </div>
        );
    };

    if (!props.event) return null;
    const showProMod = !!props.event.pro_mod_open_date;
    const event = props.event;
    const startDate = event ? new Date(event.start_date) : new Date();

    return (
        <div
            style={{
                background: darkMode ? '#111' : '#fff',
                borderRadius: 20,
                padding: isMdOrUp ? 32 : 8,
                margin: isMdOrUp ? 24 : 8,
                width: isMdOrUp ? '80vw': '95vw',
                boxSizing: 'border-box',
                boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <Snackbar anchorOrigin={{vertical: "top", horizontal: "center"}} open={open} onClose={() => setOpen(false)} autoHideDuration={6000}>
                <Alert elevation={6} severity={severity} sx={{width: '100%'}} variant="filled">
                    {message}
                </Alert>
            </Snackbar>
            <Typography
                variant={isMdOrUp ? 'h2' : 'h4'}
                fontWeight="bold"
                sx={{
                    color: darkMode ? '#fff' : '#111',
                    letterSpacing: 2,
                    textAlign: 'center',
                    mb: isMdOrUp ? 4 : 2,
                    mt: isMdOrUp ? 2 : 1,
                    textShadow: darkMode ? '0 2px 8px #000' : '0 2px 8px #fff',
                }}
            >
                {props.event?.name}
            </Typography>
            <Grid
                container
                spacing={isMdOrUp ? 4 : 2}
                direction={isMdOrUp ? 'row' : 'column'}
                flexWrap="nowrap"
                justifyContent="center"
                alignItems="stretch"
                sx={{ width: '100%'}}
            >
                <Grid item xs={12} md={showProMod ? 6 : 12}>
                    {renderBracketCard('standard', darkMode, isMdOrUp)}
                </Grid>
                {showProMod && (
                    <Grid item xs={12} md={6}>
                        {renderBracketCard('pro_mod', darkMode, isMdOrUp)}
                    </Grid>
                )}
            </Grid>
        </div>
    );
}